import { pickT } from "@repo/shared";
import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getWithToken } from "@/lib/api/_client";
import { useSession } from "@/lib/auth-client";
import { PublicSessionProvider } from "@/lib/context/public-session-context";
import { useUserLanguage } from "@/lib/i18n/context";
import {
  MEMBER_SESSION_TOKEN_FOR_KEY,
  MEMBER_SESSION_TOKEN_KEY,
} from "@/pages/p/member-session-storage";
import { T } from "./page.i18n";
import type { FlowInstanceData } from "./public-flow-content-inner";
import { PublicFlowContentInner } from "./public-flow-content-inner";
import { PublicFlowProvider } from "./public-flow-context";

export type PublicFlowViewPageProps = {
  /**
   * QL-505 — set on the `/p/m/:token/flow` route. `params.token` is
   * then the MEMBER token, not the group's own public token, and the session
   * is stored under a member-scoped localStorage key so it can never collide
   * with (or be mistaken for) a group session — see `MEMBER_SESSION_*` below.
   * On an invalid/expired session this route always sends the client back to
   * the member's own sign-in page (`/p/m/:token`), never the group's
   * `/p/:token/auth` — the member signs in again there, with a one-time code
   * (to the member's, or their representative's, e-mail/phone) or BankID.
   */
  isMemberRoute?: boolean;
};

export default function PublicFlowViewPage({ isMemberRoute = false }: PublicFlowViewPageProps) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  // Group route: the flow instance's own public token. Member route: the
  // member-link token (`flow_instance_member_access.token`) — never the
  // group's.
  const routeToken = params.token as string;
  const sessionTokenKey = isMemberRoute ? MEMBER_SESSION_TOKEN_KEY : "publicSessionToken";
  const sessionTokenForKey = isMemberRoute
    ? MEMBER_SESSION_TOKEN_FOR_KEY
    : "publicSessionToken_for";
  const [mounted, setMounted] = useState(false);
  // QL-17 issue 027 — this page never shows flow content itself: its only
  // texts are for an advisor (their own language) and a load failure before
  // the flow is known. The flow's language takes over in PublicFlowContentInner.
  const { userLanguage } = useUserLanguage();
  const t = pickT(T, userLanguage);

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [flowInstance, setFlowInstance] = useState<FlowInstanceData>(null);

  // Avoid hydration mismatch for theme
  useEffect(() => {
    setMounted(true);
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Better Auth advisor session, shared app-wide via the session atom
  const { data: advisorSession, isPending: advisorSessionPending } = useSession();
  const isAdvisor = !!advisorSession?.user;

  // Check for session token and load flow instance
  useEffect(() => {
    // Wait until the advisor session check has settled
    if (advisorSessionPending) return;

    if (isAdvisor) {
      // Advisor is logged in - they should NOT use public links
      setError(t.advisorWarning);
      setLoading(false);
      return;
    }

    // Not logged in as advisor - proceed with public session
    proceedWithPublicSession();

    function redirectToAuth() {
      // QL-505 — a member session always goes back to its own sign-in
      // page (one-time code or BankID), never the group's auth page: the
      // group route requires the group's own public token, which this route
      // never has.
      navigate(isMemberRoute ? `/p/m/${routeToken}` : `/p/${routeToken}/auth`);
    }

    function proceedWithPublicSession() {
      // Check if session token is in URL (from Criipto callback)
      const urlSessionToken = searchParams.get("session");

      if (urlSessionToken) {
        // Store it in localStorage and remove from URL
        localStorage.setItem(sessionTokenKey, urlSessionToken);
        // Also store the (member or group) token it's associated with
        localStorage.setItem(sessionTokenForKey, routeToken);
        // Remove session param from URL
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }

      // Check if the stored session token is for THIS link
      const storedToken = localStorage.getItem(sessionTokenKey);
      const storedFor = localStorage.getItem(sessionTokenForKey);

      // If we have a session token but it's for a different link, clear it
      if (storedToken && storedFor && storedFor !== routeToken) {
        localStorage.removeItem(sessionTokenKey);
        localStorage.removeItem(sessionTokenForKey);
      }

      const token = urlSessionToken || (storedFor === routeToken ? storedToken : null);

      if (!token) {
        redirectToAuth();
        return;
      }

      setSessionToken(token);

      // Load the (now-lean) flow instance row. `flow`, `client`, and `tenant`
      // are no longer bundled here — `PublicFlowContentInner` fetches each
      // via its own per-resource hook (`usePublicFlow{Definition,Client,Tenant}`)
      // and runs the product-types fetch off the definition's collection id.
      getWithToken<FlowInstanceData>("/public/flow/instance", token)
        .then((data) => {
          // An invalid session goes straight back to sign-in (in the flow's
          // language there); the spinner stays up meanwhile.
          if (!data) {
            localStorage.removeItem(sessionTokenKey);
            localStorage.removeItem(sessionTokenForKey);
            redirectToAuth();
            return;
          }

          setFlowInstance(data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem(sessionTokenKey);
          localStorage.removeItem(sessionTokenForKey);
          redirectToAuth();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeToken, isMemberRoute, navigate, searchParams, advisorSessionPending, isAdvisor]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !flowInstance || !sessionToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || t.failedToLoad}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Conditional-visibility virtual fields are no longer derived here from a
  // bundled `/public/flow/instance` response. Both `customFieldValues` (saved
  // custom field values) and the calculatedData blobs (client_classification,
  // sumsub_verification) are now sourced reactively in
  // `public-flow-content-inner.tsx` via dedicated React Query hooks:
  // `usePublicFieldValues`, `usePublicClientClassificationValue`,
  // `usePublicSumsubVerificationValue`. The `__trapets_pep__` /
  // `__trapets_sanctioned__` derivations are removed entirely — Trapets is
  // advisor-only by design and is gated out of the public engine traversal
  // via D39. Client country is read directly off the dedicated client hook
  // inside the orchestrator's `useMemo`.
  //
  // `UserLanguageProvider` lives inside `PublicFlowContentInner` now —
  // language comes from `usePublicFlowDefinition` rather than the legacy
  // `/flow/instance` response, so the provider has to be inside the React
  // Query tree to read it.

  return (
    <PublicSessionProvider sessionToken={sessionToken}>
      <PublicFlowProvider>
        <PublicFlowContentInner
          flowInstance={flowInstance}
          setFlowInstance={setFlowInstance}
          saving={saving}
          setSaving={setSaving}
          mounted={mounted}
          navigate={navigate}
          isMemberSession={isMemberRoute}
        />
      </PublicFlowProvider>
    </PublicSessionProvider>
  );
}
